import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown } from 'lucide-react';
import { MaskedLines, Reveal, Marquee } from '../components/motion';
import SEO from '../components/SEO';
import { SectionTag, ArrowBtn, TextLink, Countdown, mediaUrl, isVideo } from '../components/bits';
import { useLead } from '../components/LeadModal';
import { useContent } from '../lib/content';
import { CORE_MESSAGES, SCHOLARSHIP_DATE, SCHOLARSHIP_LABEL, ARTICLES_DATA } from '../content/homeData';

function Hero() {
  const ref = useRef(null);
  const { ct } = useContent();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const yOrb = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const { open } = useLead();
  const badge = ct('home.hero.badge', 'Digital Intelligence & Technology Excellence Council');
  const heroImage = ct('home.hero.image', null);
  const l1 = ct('home.hero.line1', null);
  const l2 = ct('home.hero.line2', null);
  const l3 = ct('home.hero.line3', null);

  return (
    <section ref={ref} data-testid="home-hero" className="bg-grid-dark relative flex min-h-[100svh] items-center overflow-hidden bg-navy text-white">
      <motion.div style={{ y: yOrb }} className="absolute -right-32 top-[-15%] h-[560px] w-[560px] rounded-full bg-electric/30 blur-[160px]" />
      <motion.div style={{ y: yOrb }} className="absolute -left-40 bottom-[-25%] h-[420px] w-[420px] rounded-full bg-gold/10 blur-[140px]" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 select-none overflow-hidden">
        <span className="block translate-y-[30%] text-center text-[26vw] font-bold leading-none tracking-tighter text-white/[0.03]">DITEC</span>
      </div>
      <motion.div style={{ y: yText }} className="wrap relative w-full pb-28 pt-40">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
          <span data-testid="hero-badge" className="overline inline-flex items-center gap-2 rounded-full bg-white/5 px-4 py-2 !text-[10px] text-gold sm:!text-xs">
            {badge}
          </span>
        </motion.div>
        {heroImage ? (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }} className="mt-8">
            <div data-testid="hero-image-home" className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl shadow-black/40">
              {isVideo(heroImage) ? (
                <video src={mediaUrl(heroImage)} autoPlay muted loop playsInline className="h-auto w-full" />
              ) : (
                <img src={mediaUrl(heroImage)} alt="DITEC hero" className="h-auto w-full" />
              )}
            </div>
          </motion.div>
        ) : (
          <MaskedLines
            className="mt-8"
            lineClassName="text-display font-semibold"
            lines={[
              l1 ?? <span key="1">Building <span className="font-serif italic font-normal text-gold">Digital Talent.</span></span>,
              l2 ?? <span key="2">Powering <span className="font-serif italic font-normal text-gold">Enterprise.</span></span>,
              l3 ?? <span key="3">Connecting <span className="text-electric-light">All.</span></span>,
            ]}
          />
        )}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.7 }}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
            {ct('home.hero.intro', 'DITEC brings together next-generation digital learning and enterprise capability within one connected ecosystem. For students, that means learning by building real digital assets, developing practical skills and creating evidence of what they can do. For enterprises, it means access to emerging talent, technology, research, digital execution and strategic capabilities through a structured institutional ecosystem.')}
          </p>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.9 }} className="mt-10 flex flex-wrap items-center gap-4">
          <ArrowBtn to="/students" variant="gold" testId="hero-cta-students" style={{ backgroundColor: '#7CFC00' }}>Explore for Students</ArrowBtn>
          <ArrowBtn to="/enterprises" variant="ghostLight" testId="hero-cta-enterprises">Explore for Enterprises</ArrowBtn>
        </motion.div>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.1 }} className="mt-10 font-mono text-xs uppercase tracking-[0.25em] text-white/45">
          One Ecosystem. Multiple Pathways. Boosting Indian Economy.
        </motion.p>
      </motion.div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }} className="absolute bottom-24 left-1/2 hidden -translate-x-1/2 md:flex md:flex-col md:items-center md:gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Scroll</span>
        <ArrowDown className="h-4 w-4 animate-bounce text-gold/70" />
      </motion.div>
      <button data-testid="hero-lead-button" onClick={() => open()} className="hidden" aria-hidden="true" tabIndex={-1} />
    </section>
  );
}

export default function Home() {
  const { open } = useLead();
  const { ct, ctList, settings } = useContent();
  const schDate = settings.scholarshipDate || SCHOLARSHIP_DATE;
  const schLabel = settings.scholarshipLabel || SCHOLARSHIP_LABEL;
  return (
    <div data-testid="home-page">
    <SEO title="Home" description="DITEC brings together next-generation digital learning and enterprise capability within one connected ecosystem." />
      <Hero />
      <Marquee items={CORE_MESSAGES} />

      {/* 02 — Where Capability Meets Opportunity */}
      <section className="bg-paper py-20 sm:py-28">
        <div className="wrap grid gap-10 lg:grid-cols-[1fr_1.4fr] lg:gap-20">
          <div>
            <SectionTag>01 — The Premise</SectionTag>
            <Reveal delay={0.05}><h2 className="text-h2 mt-5 font-semibold">{ct('home.premise.heading', null) ?? <>Where Capability <span className="font-serif italic font-normal text-electric">Meets Opportunity</span></>}</h2></Reveal>
          </div>
          <div>
            <Reveal delay={0.1}>
              <p className="text-base leading-relaxed text-navy/75 sm:text-lg">The digital economy doesn't only need more people with qualifications. It needs people who can build, execute, adapt and create measurable outcomes.</p>
              <p className="mt-4 text-base leading-relaxed text-navy/75 sm:text-lg">Businesses don't only need more vendors or more résumés. They need reliable capability.</p>
              <p className="mt-6 text-h3 font-semibold text-navy">DITEC is designed to connect the two.</p>
            </Reveal>
            <Reveal delay={0.2} className="mt-8"><ArrowBtn to="/about" variant="navy" testId="cta-discover-how">Discover How DITEC Works</ArrowBtn></Reveal>
          </div>
        </div>
      </section>

      {/* Two Sides. One Ecosystem. */}
      <section className="bg-white py-20 sm:py-28">
        <div className="wrap">
          <SectionTag center>02 — Two Sides. One Ecosystem.</SectionTag>
          <div className="mt-14 grid gap-6 lg:grid-cols-2">
            <Reveal className="group flex flex-col rounded-3xl border border-navy/10 bg-paper p-8 transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-electric/10 sm:p-10">
              <span className="font-mono text-xs text-electric">FOR STUDENTS</span>
              <h3 className="text-h3 mt-4 font-semibold">Build More Than a Résumé.</h3>
              <p className="mt-4 text-sm leading-relaxed text-navy/70 sm:text-base">
                DITEC programmes combine technology, digital business, AI, cybersecurity, marketing, analytics, content and entrepreneurship with hands-on application. Students progressively build their own digital assets while developing the skills and evidence needed to enter the digital economy.
              </p>
              <ul className="mt-6 space-y-2.5">
                {['18-Month Flagship Programme', '9-Month Technical Track', '9-Month Business & Creator Track', 'Annual Scholarship Test'].map((p) => (
                  <li key={p} className="flex items-center gap-3 text-sm font-medium text-navy"><span className="h-1.5 w-1.5 rounded-full bg-electric" />{p}</li>
                ))}
              </ul>
              <div className="mt-auto pt-8"><TextLink to="/students" testId="card-cta-students">Explore For Students</TextLink></div>
            </Reveal>
            <Reveal delay={0.12} className="group flex flex-col rounded-3xl bg-navy p-8 text-white transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-navy/40 sm:p-10">
              <span className="font-mono text-xs text-gold">FOR ENTERPRISES</span>
              <h3 className="text-h3 mt-4 font-semibold">Access Capability. Create Impact.</h3>
              <p className="mt-4 text-sm leading-relaxed text-white/70 sm:text-base">
                DITEC connects enterprises with a structured ecosystem covering talent, technology, research, digital marketing, cybersecurity, advisory and institutional partnerships. Membership can also connect eligible CSR commitments with programmes designed to create measurable capability and enterprise value.
              </p>
              <ul className="mt-6 space-y-2.5">
                {['Talent Acquisition', 'CSR Routing', 'Technology & Research', 'Marketing Cell', 'Enterprise Membership'].map((p) => (
                  <li key={p} className="flex items-center gap-3 text-sm font-medium"><span className="h-1.5 w-1.5 rounded-full bg-gold" />{p}</li>
                ))}
              </ul>
              <div className="mt-auto pt-8"><TextLink to="/enterprises" dark testId="card-cta-enterprises">Explore For Enterprises</TextLink></div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Learning That Creates Evidence */}
      <section className="bg-grid-dark bg-navy py-20 text-white sm:py-28">
        <div className="wrap">
          <SectionTag dark>03 — Learning That Creates Evidence</SectionTag>
          <Reveal delay={0.05}><h2 className="text-h2 mt-5 max-w-3xl font-semibold">Learn → Build → Prove → <span className="font-serif italic font-normal text-gold">Connect</span></h2></Reveal>
          <div className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {ctList('home.journey', [
              { n: '01', t: 'Learn', d: 'Develop market-relevant digital and technology capabilities.' },
              { n: '02', t: 'Build', d: 'Create live digital properties and real projects.' },
              { n: '03', t: 'Prove', d: 'Turn performance, projects and outcomes into visible evidence.' },
              { n: '04', t: 'Connect', d: 'Move towards enterprise projects, opportunities and an expanding professional ecosystem.' },
            ]).map((s, i) => (
              <div key={s.n} data-testid={`journey-step-${s.n}`} className="group bg-navy p-8 transition-colors duration-500 hover:bg-navy-800 sm:p-10">
                <span className="font-mono text-xs text-gold/60">{s.n}</span>
                <h3 className="mt-4 text-xl font-semibold tracking-tight group-hover:text-[#00BFFF]">{s.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/60">{s.d}</p>
              </div>
            ))}
          </div>
          <Reveal className="mt-10"><ArrowBtn to="/students" variant="gold" testId="cta-student-journey">See the Student Journey</ArrowBtn></Reveal>
        </div>
      </section>

      {/* Enterprise Engagement */}
      <section className="bg-paper py-20 sm:py-28">
        <div className="wrap">
          <SectionTag>04 — Enterprise Engagement That Goes Beyond Hiring</SectionTag>
          <Reveal delay={0.05}><h2 className="text-h2 mt-5 max-w-3xl font-semibold">Talent. Technology. Intelligence. Reach. <span className="font-serif italic font-normal text-electric">Impact.</span></h2></Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
            {ctList('home.engagement', [
              { t: 'Talent', d: 'Connect with capability developed through practical work.' },
              { t: 'Technology', d: 'Access digital, AI, cybersecurity and technical expertise.' },
              { t: 'Intelligence', d: 'Use research and ecosystem data to make better decisions.' },
              { t: 'Reach', d: 'Leverage digital marketing and the wider creator ecosystem.' },
              { t: 'Impact', d: 'Connect enterprise participation with measurable social and economic outcomes.' },
            ]).map((s, i) => (
              <Reveal key={s.t} delay={i * 0.06} className="rounded-3xl border border-navy/10 bg-white p-7 transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/10">
                <h3 className="text-lg font-semibold tracking-tight text-electric">{s.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-navy/70">{s.d}</p>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10"><ArrowBtn to="/enterprises" variant="navy" testId="cta-enterprise-solutions">See Enterprise Solutions</ArrowBtn></Reveal>
        </div>
      </section>

      {/* Everything Connects */}
      <section className="bg-navy py-20 text-white sm:py-28">
        <div className="wrap grid items-center gap-12 lg:grid-cols-2">
          <div>
            <SectionTag dark>05 — Everything Connects</SectionTag>
            <Reveal delay={0.05}><h2 className="text-h2 mt-5 font-semibold">The DITEC <span className="font-serif italic font-normal text-gold">Ecosystem</span></h2></Reveal>
            <Reveal delay={0.1}>
              <ul className="mt-8 space-y-3 text-base text-white/75 sm:text-lg">
                {['Student capability becomes evidence.', 'Evidence can meet enterprise demand.', 'Projects create experience and data.', 'Data strengthens the ecosystem.', 'The ecosystem creates more opportunity.'].map((l) => (
                  <li key={l} className="flex items-start gap-3"><span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />{l}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.15} className="mt-8"><ArrowBtn to="/about" variant="gold" testId="cta-explore-ecosystem">Explore the DITEC Ecosystem</ArrowBtn></Reveal>
          </div>
          <Reveal delay={0.1} className="rounded-3xl border border-gold/20 bg-navy-800/70 p-8 text-center sm:p-12">
            <span className="overline !text-[10px] text-gold/60">Core Relationship</span>
            <p data-testid="core-relationship" className="mt-5 text-2xl font-semibold tracking-tight sm:text-3xl">
              Talent <span className="font-serif italic text-gold">↔</span> Demand <span className="font-serif italic text-gold">↔</span> Capital
            </p>
            <div className="mx-auto mt-8 h-px w-24 bg-gold/30" />
            <p className="mt-8 text-sm leading-relaxed text-white/55">The ecosystem is designed so that activity on one side strengthens the others.</p>
          </Reveal>
        </div>
      </section>

      {/* Scholarship band */}
      <section data-testid="scholarship-band" className="bg-gold py-20 text-navy sm:py-28">
        <div className="wrap grid items-center gap-10 lg:grid-cols-2">
          <div>
            <SectionTag>06 — Scholarship</SectionTag>
            <Reveal delay={0.05}><h2 className="text-h2 mt-5 font-semibold">{ct('home.scholarship.heading', null) ?? <>Your Potential <span className="font-serif italic font-normal">Deserves a Chance.</span></>}</h2></Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-navy/80 sm:text-lg">
                Every year, DITEC conducts a merit-based scholarship test designed to open access to talented students who may otherwise face financial barriers.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="mt-8 flex flex-wrap gap-4">
              <ArrowBtn to="/scholarship" variant="navy" testId="cta-scholarship-details">View Scholarship Details</ArrowBtn>
              <ArrowBtn onClick={() => open('Scholarship')} variant="ghostDark" testId="cta-register-test">Register for the Test</ArrowBtn>
            </Reveal>
          </div>
          <Reveal delay={0.1} className="rounded-3xl bg-navy p-8 text-white sm:p-10">
            <span className="overline !text-sm text-gold">Next Scholarship Test</span>
            <p className="mt-3 font-serif text-xl italic text-white/90">{schLabel}</p>
            <div className="mt-6"><Countdown target={schDate} dark testId="home-countdown" /></div>
          </Reveal>
        </div>
      </section>

      {/* Bigger Vision */}
      <section className="bg-paper py-20 sm:py-28">
        <div className="wrap max-w-4xl text-center">
          <SectionTag center>07 — The Bigger Vision</SectionTag>
          <Reveal delay={0.05}><h2 className="text-h2 mt-5 font-semibold">From Individual Capability to <span className="font-serif italic font-normal text-electric">Economic Capability.</span></h2></Reveal>
          <Reveal delay={0.1}>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-navy/75 sm:text-lg">
              {ct('home.vision.text', 'DITEC is being built with a larger ambition: to strengthen the digital talent and execution infrastructure connecting young people, enterprises, institutions and the wider digital economy.')}
            </p>
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-electric">Talent | Technology | Opportunity</p>
          </Reveal>
          <Reveal delay={0.15} className="mt-8"><ArrowBtn to="/about" variant="navy" testId="cta-about-ditec">About DITEC</ArrowBtn></Reveal>
        </div>
      </section>

      {/* Press & Insights */}
      <section className="bg-white py-20 sm:py-28">
        <div className="wrap">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <SectionTag>08 — Press & Insights</SectionTag>
              <Reveal delay={0.05}><h2 className="text-h2 mt-5 font-semibold">Ideas. Stories. <span className="font-serif italic font-normal text-electric">Digital Economy.</span></h2></Reveal>
            </div>
            <TextLink to="/press" testId="cta-visit-press">Visit Press & Insights</TextLink>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {ARTICLES_DATA.slice(0, 3).map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.08}>
                <Link to={`/press/${a.slug}`} data-testid={`press-card-${i}`} className="group flex h-full flex-col rounded-3xl border border-navy/10 bg-paper p-8 transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-navy/10">
                  <span className="overline !text-[10px] text-electric">{a.kicker}</span>
                  <h3 className="mt-4 text-lg font-semibold leading-snug tracking-tight group-hover:text-electric">{a.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-navy/65">{a.summary}</p>
                  <span className="mt-auto pt-6 font-mono text-xs text-navy/45">{a.date} · {a.readTime}</span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-grid-dark bg-navy py-20 text-white sm:py-32">
        <div className="wrap">
          <SectionTag dark center>09 — Begin</SectionTag>
          <Reveal delay={0.05}><h2 className="text-h2 mt-5 text-center font-semibold">Where Do You Want to <span className="font-serif italic font-normal text-gold">Begin?</span></h2></Reveal>
          <div className="mx-auto mt-14 grid max-w-4xl gap-6 md:grid-cols-2">
            <Reveal className="rounded-3xl border border-white/10 bg-navy-800/70 p-8 transition-colors duration-500 hover:border-gold/40 sm:p-10">
              <h3 className="text-h3 font-semibold">I Want to Learn</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/65">Explore programmes, scholarships, digital assets and the student journey.</p>
              <div className="mt-7"><ArrowBtn to="/students" variant="gold" testId="final-cta-students" style={{ backgroundColor: '#3FFF00' }}>For Students</ArrowBtn></div>
            </Reveal>
            <Reveal delay={0.1} className="rounded-3xl border border-white/10 bg-navy-800/70 p-8 transition-colors duration-500 hover:border-gold/40 sm:p-10">
              <h3 className="text-h3 font-semibold">I Want to Build</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/65">Explore enterprise membership, talent, technology, research and digital solutions.</p>
              <div className="mt-7"><ArrowBtn to="/enterprises" variant="gold" testId="final-cta-enterprises" style={{ backgroundColor: '#3FFF00' }}>For Enterprises</ArrowBtn></div>
            </Reveal>
          </div>
          <Reveal className="mt-10 text-center"><TextLink to="/contact" dark testId="final-cta-contact">Contact DITEC</TextLink></Reveal>
        </div>
      </section>
    </div>
  );
}
